import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngxs/store';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';
import Project from 'src/app/models/Project';
import { Timeslot } from 'src/app/models/Timeslot';

import { AddTimeslot, UpdateTimeSlot } from '../timesheets.actions';
import { EmployeeService } from '../../employees/employees.service';
import { TimesheetsService } from '../timesheets.service';
import { TimesheetAiValidatorService } from '../timesheet-ai-validator.service';
import * as moment from 'moment';

@Component({
    selector: 'app-edit-timeslot-modal',
    templateUrl: './edit-timeslot-modal.component.html',
    styleUrls: ['./edit-timeslot-modal.component.scss'],
})
export class EditTimeslotModalComponent implements OnInit, OnDestroy {
    @Input() employeeId: string;
    @Input() timesheetUuid: string;
    @Input() timeslot: Timeslot;
    projects: Project[];
    allProjects: Project[];
    linkedProjects: Project[];
    timeslotFormgroup: FormGroup;
    isLoading = true;
    oldPdf = '';
    loggedIn = false;
    showAllActivated = false;
    DESCRIPTION_MAX_LENGTH = 250;
    currentAmountOfDescriptionCharacters: number;
    descriptionIsTooLong = false;
    private subscriptions = new Subscription();

    // Validation UI state
    validationInProgress = false;
    validationProgress = 0;
    validationTimer?: any;
    validationData: { consultantName?: string; timesheetType?: string; periodISO?: string; signed?: boolean; hours?: number } = {};

    constructor(
        private store: Store,
        private fb: FormBuilder,
        public activeModal: NgbActiveModal,
        private toastr: ToastrService,
        private logger: NGXLogger,
        private employeeService: EmployeeService,
        private timesheetsService: TimesheetsService,
        private tsAiValidator: TimesheetAiValidatorService,
    ) {}

    ngOnInit(): void {
        this.loggedIn = localStorage.getItem('token') != null;

        // Fetch projects: linked (by employee). Fetch all projects only for logged-in users.
        this.logger.debug('Fetching linked projects for employee');
        this.subscriptions.add(
            this.employeeService.getAllLinkedProjects(this.employeeId).subscribe(
                (linked) => {
                    // Deduplicate linked projects by UUID to avoid duplicates when multiple price entries exist
                    this.linkedProjects = this.uniqueProjects(linked || []);
                    // Default to linked projects; public links should not query all projects
                    this.projects = this.linkedProjects;
                    this.isLoading = false;
                },
                (err) => {
                    this.logger.error('Failed to fetch linked projects for employee');
                    this.toastr.error('Kon gelinkte projecten niet laden. Probeer later opnieuw.');
                    this.linkedProjects = [];
                    this.projects = [];
                    this.isLoading = false;
                },
            ),
        );
        // Defer fetching ALL projects until the user toggles it, supports public token endpoint
        this.timeslotFormgroup = this.fb.group({
            project: ['', [Validators.required]],
            description: [''],
            hours: [0, [Validators.required, Validators.min(0)]],
            minutes: [0, [Validators.required, Validators.max(60), Validators.min(0)]],
            leaveHours: [0, [Validators.min(0)]],
            pdfUrl: [''],
            showAllActivated: false,
        });

        if (this.timeslot != undefined) {
            this.oldPdf = this.timeslot.pdfUrl;
            const actualMinutes = this.timeslot.minutes % 60;
            const actualHours = (this.timeslot.minutes - actualMinutes) / 60;
            this.timeslotFormgroup.setValue({
                project: this.timeslot.project ? this.timeslot.project : '',
                description: this.timeslot.description,
                hours: actualHours,
                minutes: actualMinutes,
                leaveHours: Math.round((this.timeslot.leaveMinutes || 0) / 60),
                pdfUrl: this.timeslot.pdfUrl,
                showAllActivated: this.showAllActivated
            });

            // Prefill validation data if already saved
            this.validationData = {
                consultantName: this.timeslot.validatedConsultantName,
                timesheetType: this.timeslot.validatedTimesheetType,
                periodISO: this.timeslot.validatedPeriodISO,
                signed: this.timeslot.validatedSigned,
                hours: this.timeslot.validatedHours,
            };
            // Also fetch fresh from backend (token-aware) to ensure latest values
            if (this.timeslot?.uuid) {
                this.subscriptions.add(
                    this.timesheetsService.fetchTimeslotValidation(this.timeslot.uuid).subscribe(
                        (v) => {
                            this.validationData = {
                                consultantName: v?.consultantName,
                                timesheetType: v?.timesheetType,
                                periodISO: v?.periodISO,
                                signed: v?.signed,
                                hours: typeof v?.hours === 'number' ? v.hours : undefined,
                            };
                        },
                        () => {}
                    )
                );
            }
        }

        // Auto-start validation after a new upload is set on the pdfUrl control
        const pdfCtrl = this.timeslotFormgroup.get('pdfUrl');
        if (pdfCtrl) {
            this.subscriptions.add(
                pdfCtrl.valueChanges.subscribe((val) => {
                    if (val && typeof val === 'string' && val.trim().length > 0) {
                        // Avoid triggering on the initial setValue by checking against the old value
                        if (!this.oldPdf || (this.oldPdf && this.oldPdf !== val)) {
                            this.validateAttachment();
                        }
                    }
                })
            );
        }
    }

    addTimeslot() {
        const minutes = this.timeslotFormgroup.get('minutes').value + this.timeslotFormgroup.get('hours').value * 60;
        if (this.timeslot == undefined) {
            this.logger.debug('Attempt to add timeslot');
            this.subscriptions.add(
                this.store
                    .dispatch(
                        new AddTimeslot(
                            this.timesheetUuid,
                            this.timeslotFormgroup.get('project').value.uuid,
                            minutes,
                            this.timeslotFormgroup.get('pdfUrl').value,
                            this.timeslotFormgroup.get('description').value,
                        ),
                    )
                    .subscribe(
                        (res) => {
                            this.logger.debug('Succesfully added timeslot');
                            this.toastr.success('Timeslot added.');
                            this.activeModal.close();
                        },
                        (err) => {
                            this.logger.error('Failed to add timeslot');
                            this.toastr.error('Oops, something went wrong. Please try again later.');
                        },
                    ),
            );
        } else {
            const newTimeslot = JSON.parse(JSON.stringify(this.timeslot));
            newTimeslot.minutes = minutes;
            newTimeslot.leaveMinutes = Number(this.timeslotFormgroup.get('leaveHours').value || 0) * 60;
            newTimeslot.description = this.timeslotFormgroup.get('description').value;
            newTimeslot.pdfUrl = this.timeslotFormgroup.get('pdfUrl').value;
            if (this.timeslotFormgroup.get('project').value.uuid !== newTimeslot.project?.uuid) {
                newTimeslot.project = this.projects.find((p) => p.uuid === this.timeslotFormgroup.get('project').value.uuid);
            }
            this.logger.debug('Attempt to update timeslot info');
            this.subscriptions.add(
                this.store.dispatch(new UpdateTimeSlot(this.timesheetUuid, newTimeslot)).subscribe(
                    (res) => {
                        this.logger.debug('Succesfully updated timeslot info');
                        this.toastr.success('Timeslot updated.');
                        this.activeModal.close();
                    },
                    (err) => {
                        this.logger.error('Failed to update timeslot info');
                        this.toastr.error('Oops, something went wrong. Please try again later.');
                    },
                ),
            );
        }
    }

    toggleShowAllProjects() {
        // Read selected value from the radio form control for consistency
        const raw = this.timeslotFormgroup.get('showAllActivated').value;
        const selected = raw === true || raw === 'true';
        this.showAllActivated = selected;

        if (selected) {
            // When "All projects" is chosen, fetch full list if not yet loaded
            if (!this.allProjects || this.allProjects.length === 0) {
                this.logger.debug('Fetching ALL projects for timesheet (token-aware)');
                this.subscriptions.add(
                    this.employeeService.getAllProjectsProtected().subscribe(
                        (all) => {
                            this.allProjects = this.uniqueProjects(all || []);
                            this.projects = this.allProjects;
                        },
                        (err) => {
                            this.logger.error('Failed to fetch ALL projects');
                            this.toastr.error('Kon alle projecten niet laden. Probeer later opnieuw.');
                            this.projects = this.linkedProjects;
                            this.showAllActivated = false;
                            this.timeslotFormgroup.get('showAllActivated').setValue(false);
                        },
                    ),
                );
            } else {
                this.projects = this.allProjects;
            }
        } else {
            // Back to linked-only
            this.projects = this.linkedProjects;
        }
    }

    promptLogin() {}

    onDescriptionKeyUp() {
        this.descriptionIsTooLong = false;
        this.currentAmountOfDescriptionCharacters = this.timeslotFormgroup.controls.description.value.length;
        if (this.currentAmountOfDescriptionCharacters > this.DESCRIPTION_MAX_LENGTH) {
            this.descriptionIsTooLong = true;
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    // Ensure uniqueness by project UUID (and keep first occurrence)
    private uniqueProjects(list: Project[]): Project[] {
        const seen = new Set<string>();
        const result: Project[] = [];
        for (const p of list || []) {
            const id = p?.uuid ?? '';
            if (!id) continue;
            if (!seen.has(id)) {
                seen.add(id);
                result.push(p);
            }
        }
        return result;
    }

    // ===== Validation logic =====
    hasExistingValidation(): boolean {
        return !!(this.validationData?.consultantName || this.validationData?.timesheetType || this.validationData?.periodISO || (this.validationData?.signed !== undefined) || (this.validationData?.hours !== undefined));
    }

    private computeExpectedHours(): number {
        try {
            const minutes = Number(this.timeslotFormgroup.get('minutes')?.value || 0);
            const hours = Number(this.timeslotFormgroup.get('hours')?.value || 0);
            const leaveHours = Number(this.timeslotFormgroup.get('leaveHours')?.value || 0);
            const totalHours = (minutes + (hours * 60)) / 60 + leaveHours;
            const invoiceByDay = !!this.timeslotFormgroup.get('project')?.value?.invoiceByDay;
            const hoursPerDay = Number(this.timeslotFormgroup.get('project')?.value?.hoursPerDay ?? 8);
            const daysWorked = totalHours / 8;
            return invoiceByDay ? Number((daysWorked * hoursPerDay).toFixed(2)) : Number(totalHours.toFixed(2));
        } catch { return 0; }
    }

    startProgress(): void {
        this.validationProgress = 10;
        this.validationTimer = setInterval(() => {
            this.validationProgress = Math.min(95, this.validationProgress + 7);
        }, 300);
    }

    stopProgress(): void {
        if (this.validationTimer) clearInterval(this.validationTimer);
        this.validationProgress = 100;
        setTimeout(() => { this.validationProgress = 0; }, 800);
    }

    validateAttachment(): void {
        try {
            const pdfUrl = this.timeslotFormgroup.get('pdfUrl')?.value || '';
            if (!pdfUrl) {
                this.toastr.info('Voeg eerst een PDF toe.');
                return;
            }
            const employeeName = '';
            const expectedHours = this.computeExpectedHours();
            const uploadedMonthISO = null; // let AI detect from filename if possible
            const timesheetTypeHint = (this.timeslotFormgroup.get('project')?.value?.timesheetType || '').toString().trim();

            this.validationInProgress = true;
            this.startProgress();
            this.tsAiValidator
                .validate(pdfUrl, {
                    employeeName,
                    expectedHours,
                    monthLabel: undefined,
                    uploadedMonthISO: uploadedMonthISO as any,
                    timesheetTypeHint,
                })
                .then((result) => {
                    const payload = {
                        consultantName: employeeName || undefined,
                        timesheetType: (result.type || timesheetTypeHint || '').trim() || undefined,
                        periodISO: (result.details?.pdfMonthISO as any) || undefined,
                        signed: result.details?.signed,
                        hours: typeof result.details?.parsedHours === 'number' ? result.details.parsedHours : expectedHours,
                    };
                    if (this.timeslot?.uuid) {
                        this.subscriptions.add(
                            this.timesheetsService.saveTimeslotValidation(this.timeslot.uuid, payload).subscribe(
                                (updated) => {
                                    const refreshed = updated?.timeslots?.find((t) => t.uuid === this.timeslot.uuid);
                                    if (refreshed) {
                                        this.validationData = {
                                            consultantName: refreshed.validatedConsultantName,
                                            timesheetType: refreshed.validatedTimesheetType,
                                            periodISO: refreshed.validatedPeriodISO,
                                            signed: refreshed.validatedSigned,
                                            hours: refreshed.validatedHours,
                                        };
                                    } else {
                                        this.validationData = payload;
                                    }
                                    this.toastr.success('Validatie opgeslagen.');
                                },
                                () => {
                                    this.toastr.error('Opslaan van validatie mislukt.');
                                }
                            )
                        );
                    }
                })
                .catch(() => {
                    this.toastr.error('AI-validatie kon niet worden uitgevoerd.');
                })
                .finally(() => {
                    this.validationInProgress = false;
                    this.stopProgress();
                });
        } catch {
            this.toastr.error('Kon timesheet niet valideren.');
        }
    }
}
